import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * VideoThumbnail component that shows a thumbnail with play button
 * and loads the video iframe when clicked
 * 
 * @param {Object} props Component props
 * @param {string} props.thumbnailUrl URL to the thumbnail image
 * @param {string} props.videoEmbedUrl URL to the video embed iframe
 * @param {string} props.title Title/alt text for the video
 * @returns {React.ReactElement} VideoThumbnail component
 */
const VideoThumbnail = ({ thumbnailUrl, videoEmbedUrl, title }) => {
  const [showVideo, setShowVideo] = useState(false);

  const handleClick = () => {
    setShowVideo(true);
  };

  return (
    <div className="rounded-2xl overflow-hidden shadow-card relative">
      {!showVideo ? (
        <div className="relative group cursor-pointer" onClick={handleClick}>
          <img 
            src={thumbnailUrl} 
            alt={`${title} thumbnail`} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col items-center justify-center opacity-80 group-hover:opacity-95 transition-all duration-300">
            <div className="bg-brand-blue-600 rounded-full p-4 mb-3 transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
              <svg 
                className="w-8 h-8 text-white" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            </div>
            <span className="text-white font-medium bg-black bg-opacity-50 px-4 py-1 rounded-full">Click to play video</span>
          </div>
        </div>
      ) : (
        <div style={{ position: 'relative', paddingTop: '56.25%' }}>
          <iframe
            src={videoEmbedUrl}
            loading="lazy"
            style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
            allowFullScreen={true}
            title={title}
          ></iframe>
        </div>
      )}
    </div>
  );
};

VideoThumbnail.propTypes = {
  thumbnailUrl: PropTypes.string.isRequired,
  videoEmbedUrl: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};

export default VideoThumbnail;
